import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const SUPPORTED_LANGUAGES = ['en', 'ar', 'fr', 'es', 'ur'];
const RTL_LANGUAGES = ['ar', 'ur'];
const FONT_SIZE_MAP = { small: '14px', medium: '16px', large: '19px' };

// Apply font size immediately on module load
const applyFontSize = (size) => {
  document.documentElement.style.fontSize = FONT_SIZE_MAP[size] || '16px';
};
applyFontSize(localStorage.getItem('fontSize') || 'medium');

// ─── Palette definitions ────────────────────────────────────────────────
export const PALETTES = {
  navy: {
    id: 'navy',
    name: 'Midnight Navy',
    swatch: '#1a365d',
    vars: {
      '--p-hex': '#1a365d',
      '--p-light-hex': '#2d5a87',
      '--p-dark-hex': '#0f2744',
      '--card-dark-from': '#1e3a5f',
      '--card-dark-to': '#0f2744',
      '--bg-light-from': '#f0f9ff',
      '--bg-light-via': '#e0f2fe',
      '--bg-dark-from': '#0f172a',
      '--bg-dark-via': '#1e293b',
    },
  },
  emerald: {
    id: 'emerald',
    name: 'Serene Emerald',
    swatch: '#047857',
    vars: {
      '--p-hex': '#047857',
      '--p-light-hex': '#059669',
      '--p-dark-hex': '#064e3b',
      '--card-dark-from': '#065f46',
      '--card-dark-to': '#022c22',
      '--bg-light-from': '#ecfdf5',
      '--bg-light-via': '#d1fae5',
      '--bg-dark-from': '#022c22',
      '--bg-dark-via': '#064e3b',
    },
  },
  terracotta: {
    id: 'terracotta',
    name: 'Desert Sand',
    swatch: '#9a3412',
    vars: {
      '--p-hex': '#9a3412',
      '--p-light-hex': '#c2410c',
      '--p-dark-hex': '#431407',
      '--card-dark-from': '#7c2d12',
      '--card-dark-to': '#431407',
      '--bg-light-from': '#fff7ed',
      '--bg-light-via': '#ffedd5',
      '--bg-dark-from': '#431407',
      '--bg-dark-via': '#7c2d12',
    },
  },
  slate: {
    id: 'slate',
    name: 'Modern Slate',
    swatch: '#475569',
    vars: {
      '--p-hex': '#334155',
      '--p-light-hex': '#475569',
      '--p-dark-hex': '#0f172a',
      '--card-dark-from': '#334155',
      '--card-dark-to': '#1e293b',
      '--bg-light-from': '#f8fafc',
      '--bg-light-via': '#e2e8f0',
      '--bg-dark-from': '#020617',
      '--bg-dark-via': '#1e293b',
    },
  },
  gold: {
    id: 'gold',
    name: 'Royal Gold',
    swatch: '#a16207',
    vars: {
      '--p-hex': '#a16207',
      '--p-light-hex': '#b45309',
      '--p-dark-hex': '#422006',
      '--card-dark-from': '#854d0e',
      '--card-dark-to': '#422006',
      '--bg-light-from': '#fefce8',
      '--bg-light-via': '#fef9c3',
      '--bg-dark-from': '#2a1b02',
      '--bg-dark-via': '#422006',
    },
  },
};

const DARK_ANCHORS = { navy: '#020617', emerald: '#01140F', terracotta: '#1A0702', slate: '#0B1220', gold: '#140C01' };

const applyPaletteVars = (paletteId) => {
  const p = PALETTES[paletteId] || PALETTES.navy;
  const root = document.documentElement;
  Object.entries(p.vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.style.setProperty('--bg-dark-anchor', DARK_ANCHORS[paletteId] || '#020617');
};

const applyBodyBackground = (isDark) => {
  // Use the palette's dark/light background for body — prevents white flash
  const bg = isDark ? 'var(--bg-dark-from)' : '#ffffff';
  document.documentElement.style.backgroundColor = bg;
  document.body.style.backgroundColor = bg;
  document.documentElement.classList.toggle('dark', isDark);
  // Near-white text in dark mode (fixes "too gray"); high-contrast slate in light
  document.documentElement.style.setProperty('--text-muted', isDark ? '#CBD5E1' : '#64748B');
  document.documentElement.style.setProperty('--text-subtle', isDark ? '#E2E8F0' : '#334155');
};

// Apply palette + background immediately on module load (before React renders)
const _savedPalette = localStorage.getItem('colorPalette') || 'navy';
const _savedTheme = localStorage.getItem('theme') || 'light';
applyPaletteVars(_savedPalette);
applyBodyBackground(_savedTheme === 'dark');

// ─── Context ────────────────────────────────────────────────────────────
const ThemeContext = createContext(null);
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const s = localStorage.getItem('theme');
    return s ? s === 'dark' : false;
  });

  const [language, setLanguageState] = useState(() => {
    const s = localStorage.getItem('language');
    return SUPPORTED_LANGUAGES.includes(s) ? s : 'en';
  });

  const [use24Hour, setUse24Hour] = useState(() => {
    const s = localStorage.getItem('timeFormat');
    return s ? s === '24' : false;
  });

  const [weekStartDay, setWeekStartDay] = useState(() => {
    const s = localStorage.getItem('weekStartDay');
    return s ? parseInt(s) : 6;
  });

  const [palette, setPaletteState] = useState(() => {
    const s = localStorage.getItem('colorPalette');
    return PALETTES[s] ? s : 'navy';
  });

  const [fontSize, setFontSizeState] = useState(() => {
    const s = localStorage.getItem('fontSize');
    return ['small', 'medium', 'large'].includes(s) ? s : 'medium';
  });

  const [travelMode, setTravelModeState] = useState(() =>
    localStorage.getItem('travelMode') === 'true'
  );

  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Sync font size CSS + localStorage
  useEffect(() => {
    applyFontSize(fontSize);
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('travelMode', String(travelMode));
  }, [travelMode]);

  // Sync dark mode: update body/html background + localStorage
  useEffect(() => {
    applyBodyBackground(isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Also update body background when palette changes (dark variant uses palette color)
  useEffect(() => {
    applyPaletteVars(palette);
    localStorage.setItem('colorPalette', palette);
    // Re-apply body background with the new palette variable value
    applyBodyBackground(isDarkMode);
  }, [palette, isDarkMode]);

  // Sync language
  useEffect(() => {
    if (i18n.language !== language) i18n.changeLanguage(language);
    localStorage.setItem('language', language);
    document.documentElement.dir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => { localStorage.setItem('timeFormat', use24Hour ? '24' : '12'); }, [use24Hour]);
  useEffect(() => { localStorage.setItem('weekStartDay', weekStartDay.toString()); }, [weekStartDay]);

  const setLanguage = useCallback((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) setLanguageState(lang);
  }, []);

  // ─── Backend prefs ────────────────────────────────────────────────────
  const savePreferences = useCallback(async (newPrefs) => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    try {
      await fetch(`${BACKEND_URL}/api/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(newPrefs),
      });
    } catch (e) { console.error('Failed to save preferences:', e); }
  }, []);

  const loadPreferences = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) { setPrefsLoaded(true); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/preferences`, { headers: getAuthHeaders() });
      if (res.ok) {
        const p = await res.json();
        if (p.week_start_day !== undefined) { setWeekStartDay(p.week_start_day); localStorage.setItem('weekStartDay', p.week_start_day.toString()); }
        if (p.theme) { setIsDarkMode(p.theme === 'dark'); localStorage.setItem('theme', p.theme); }
        if (p.language && SUPPORTED_LANGUAGES.includes(p.language)) setLanguage(p.language);
        if (p.time_format) { setUse24Hour(p.time_format === '24'); localStorage.setItem('timeFormat', p.time_format); }
        if (p.color_palette && PALETTES[p.color_palette]) { setPaletteState(p.color_palette); }
        if (p.font_size && ['small','medium','large'].includes(p.font_size)) { setFontSizeState(p.font_size); }
        if (p.travel_mode !== undefined) {
          setTravelModeState(!!p.travel_mode);
          localStorage.setItem('travelMode', String(!!p.travel_mode));
        }
      }
    } catch (e) { console.error('Failed to load preferences:', e); }
    finally { setPrefsLoaded(true); }
  }, [setLanguage]);

  useEffect(() => { loadPreferences(); }, [loadPreferences]);

  // ─── Action helpers (always include all prefs when saving) ────────────
  const getCurrentPrefs = useCallback(() => ({
    theme: isDarkMode ? 'dark' : 'light',
    language,
    time_format: use24Hour ? '24' : '12',
    week_start_day: weekStartDay,
    color_palette: palette,
    font_size: fontSize,
    travel_mode: travelMode,
  }), [isDarkMode, language, use24Hour, weekStartDay, palette, fontSize, travelMode]);

  const toggleTheme = () => setIsDarkMode(prev => {
    const next = !prev;
    savePreferences({ ...getCurrentPrefs(), theme: next ? 'dark' : 'light' });
    return next;
  });

  const toggleLanguage = () => {
    const next = language === 'ar' ? 'en' : 'ar';
    changeLanguage(next);
  };

  const changeLanguage = (lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    setLanguage(lang);
    savePreferences({ ...getCurrentPrefs(), language: lang });
  };

  const toggleTimeFormat = () => setUse24Hour(prev => {
    const next = !prev;
    savePreferences({ ...getCurrentPrefs(), time_format: next ? '24' : '12' });
    return next;
  });

  const updateWeekStartDay = (day) => {
    setWeekStartDay(day);
    savePreferences({ ...getCurrentPrefs(), week_start_day: day });
  };

  const changePalette = (id) => {
    if (!PALETTES[id]) return;
    setPaletteState(id);
    savePreferences({ ...getCurrentPrefs(), color_palette: id });
  };

  const changeFontSize = (size) => {
    if (!['small', 'medium', 'large'].includes(size)) return;
    setFontSizeState(size);
    savePreferences({ ...getCurrentPrefs(), font_size: size });
  };

  const toggleTravelMode = () => {
    setTravelModeState(prev => !prev);
    savePreferences({ ...getCurrentPrefs(), travel_mode: !travelMode });
  };

  const cyclePalette = useCallback(() => {
    const ids = Object.keys(PALETTES);
    const nextId = ids[(ids.indexOf(palette) + 1) % ids.length];
    changePalette(nextId);
  }, [palette]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (time24) => {
    if (!time24) return '';
    if (use24Hour) return time24;
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const h12 = hours % 12 || 12;
    return `${h12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const isRTL = RTL_LANGUAGES.includes(language);

  return (
    <ThemeContext.Provider value={{
      isDarkMode, toggleTheme,
      language, setLanguage: changeLanguage, toggleLanguage,
      isArabic: language === 'ar', isRTL,
      use24Hour, toggleTimeFormat,
      formatTime,
      weekStartDay, updateWeekStartDay,
      palette, changePalette, cyclePalette, palettes: PALETTES,
      fontSize, changeFontSize, fontSizeMap: FONT_SIZE_MAP,
      travelMode, toggleTravelMode,
      prefsLoaded,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
